import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Divider,
  IconButton,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formFieldStyles, labelStyles, dialogTitleStyles, dialogDescriptionStyles } from "../../styles/formStyles";
import { FormErrors } from "../../interfaces/Errors";
import { useAuth } from "../../auth/AuthProvider";
import { ICreateDepartmentDTO, IUpdateDepartmentDTO } from "../../interfaces/UserManagement";
import { createDepartment, updateDepartment } from "../../services/UserManagement";

interface DepartmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editingDept: any;
}

const DepartmentDialog = ({open,onClose,onSave,editingDept}: DepartmentDialogProps)=> {
    const {getLoginUserInfo} = useAuth();
    const loggedInUser = getLoginUserInfo();
    const [departmentName, setDepartmentName] = useState("");
    const [errors, setErrors] = useState<FormErrors>({});
    const [msg, setMsg] = useState("");
    const [openError, setOpenError] = useState(false);
    const [openSuccess, setOpenSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const warehouseId = loggedInUser?.workspaceId;
    const companyId = loggedInUser?.companyId;

    useEffect(() => {
      if (!open) return;
      if (editingDept) {
        setDepartmentName(editingDept.departmentName || "");
      } else {
        setDepartmentName("");
      }
      setErrors({});
    }, [open, editingDept]);

    const validate = () => {
      const newErrors: FormErrors = {};
    
      if (!departmentName.trim()) newErrors.roleName = "Department name is required";
    
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };
    
      const handleSave = async () => {
        if (editingDept) {
          await handleUpdate();
        } else {
          await handleCreate();
        }
      };

      const handleCreate =  async() => {
        if (!validate()) return;
        if (!companyId || !warehouseId) {
          setOpenError(true);
          setMsg("Company or Warehouse information is missing.");
          return;
        }  
        setLoading(true); 
        try {
          const post = {} as ICreateDepartmentDTO;
          post.departmentName = departmentName;
          post.workspaceId = warehouseId;
          post.companyId = companyId;
          
          const response = await createDepartment(post);
          console.log("response", response);
          if(response && response.status === "success"){
            setOpenSuccess(true);
            setMsg("New Department created Successfully.");
            resetForm();
            onClose();
            onSave();
          }else{
            setOpenError(true);
            setMsg("Error adding department.");
          }
        }catch(error) {
          setOpenError(true);
          setMsg("Error exception modifying saving department.");
        }finally{
          setLoading(false);
        }
      };

      const handleUpdate = async () => {
        if (!validate()) return;
        if (!warehouseId) {
          setOpenError(true);
          setMsg("Warehouse information is missing.");
          return;
        }
        setLoading(true);
        try {
          const post: IUpdateDepartmentDTO = {
            departmentName,
            workspaceId: warehouseId,
          };
          const response = await updateDepartment(post, editingDept.departmentId);
          if (response && response.status === "success") {
            setOpenSuccess(true);
            setMsg("Department updated successfully.");
            resetForm();
            onClose();
            onSave();
          } else {
            setOpenError(true);
            setMsg("Error updating department.");
          }
        } catch {
          setOpenError(true);
          setMsg("Error updating department.");
        } finally {
          setLoading(false);
        }
      };
      const resetForm = () => {
        setDepartmentName("");
        setErrors({});
      };
      const handleClose = () => {
        resetForm();
        onClose();
      };
      const handleSnackbarClose = () => {
        setOpenError(false);
        setOpenSuccess(false);
      };
  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }} slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box>
          <Typography sx={dialogTitleStyles}>{editingDept ? "Edit Department" : "Add Department"}</Typography>
          <Typography sx={dialogDescriptionStyles}>{editingDept ? "Update department details" : "Add a new department"}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography component="label" sx={labelStyles}>Department Name <span style={{ color: "red" }}>*</span></Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="eg. Logistics"
              value={departmentName}
              onChange={(e) => setDepartmentName(e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              sx={formFieldStyles}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", padding: "8px 16px", textTransform: "none" }}>Cancel</Button>
        <Button onClick={handleSave} disabled={loading} variant="contained" sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset", padding: "8px 40px", textTransform: "none", "&:hover": { backgroundColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset" } }}>{loading ? <CircularProgress size={24} color="inherit" /> : "Save"}</Button>
      </DialogActions>
    </Dialog>
     <Snackbar
     anchorOrigin={{ vertical: "top", horizontal: "right" }}
     open={openError}
     autoHideDuration={5000}
     onClose={handleSnackbarClose}
   >
     <Alert severity="error" variant="filled" sx={{ width: "100%" }}>
       {msg}
     </Alert>
   </Snackbar>

   <Snackbar
     anchorOrigin={{ vertical: "top", horizontal: "right" }}
     open={openSuccess}
     autoHideDuration={5000}
     onClose={handleSnackbarClose}
   >
     <Alert severity="success" variant="filled" sx={{ width: "100%" }}>
       {msg}
     </Alert>
   </Snackbar>
   </>
  );
};
export default DepartmentDialog;
